import type { QueryClient } from '@tanstack/react-query';

import fetchWithAuth from '@/lib/apis';
import { apiEndpoint } from '@/lib/consts';
import { userProfileQueryOptions } from '@/lib/query-options';
import { rememberTabOrganization } from '@/lib/tab-organization';
import type { OrganizationDataWithMembership } from '@/lib/types';
import { getCurrentOrganizationId } from '@/store';

/**
 * The one-shot organization handoff on a link from outside the app.
 *
 * A link into a specific organization names it as `?org=`, by id or slug: the admin dashboard's Open in Glitch Ads
 * (GlitchAds/dashboard#207, #212) and the campaign links the public API and MCP server return (#6612). The route
 * guard saves that organization as the person's selection before any page loads, because the server scopes
 * organization-implicit requests, campaign reads included, by the saved selection: a switch made only in the
 * browser would open the page against the previous organization. The parameter is then dropped, since it is a
 * navigation payload and never URL state.
 *
 * The path form, `/client/{slug}`, exists since #6526 and is what links should use; the handoff stays until the
 * admin dashboard's three links have moved to it, then it goes.
 */
export const ORG_HANDOFF_PARAM = 'org';

/** The handoff a search string carries (`?org=acme`), or null when it carries none. */
export function readOrganizationHandoff(searchStr: string): string | null {
  return new URLSearchParams(searchStr).get(ORG_HANDOFF_PARAM);
}

/** The organization a handoff names, by id or slug, among the person's active memberships, or undefined. */
export function findHandoffOrganization<T extends OrganizationDataWithMembership>(
  handoff: string,
  organizations: T[],
): T | undefined {
  return organizations.find(
    (org) => org.membership?.status === 'active' && (String(org.id) === handoff || org.slug === handoff),
  );
}

/** The same address without the handoff: the path, the remaining search and the hash (given without `#`). */
export function withoutOrganizationHandoff(pathname: string, searchStr: string, hash: string): string {
  const params = new URLSearchParams(searchStr);
  params.delete(ORG_HANDOFF_PARAM);
  const search = params.toString();
  return `${pathname}${search ? `?${search}` : ''}${hash ? `#${hash}` : ''}`;
}


/**
 * Save an organization as the signed-in person's selection, or throw. The server scopes every
 * organization-implicit request by it, so the app never shows an organization the server has not accepted (#6449).
 */
export async function persistOrganizationSelection(organizationId: number): Promise<void> {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/select`, { method: 'PUT' });
  if (!response.ok) {
    throw new Error(`Organization selection was rejected (${response.status})`);
  }
}


/**
 * Open ``target`` in this tab, from a link into it (the `?org=` handoff or `/client/{slug}`), before any page loads.
 *
 * The tab remembers ``target`` as its organisation, which every request it sends names (#6773): the atom's setter
 * cannot run yet, so this is the one other writer of the tab's memory. ``target`` also becomes the saved selection,
 * what a new tab or the next sign-in opens on. Returns whether the document must reload: a tab already showing
 * another organisation holds that organisation's data in memory and starts again from ``target``.
 */
export async function openOrganizationInThisTab(
  target: OrganizationDataWithMembership,
  savedOrganizationId: number | null | undefined,
  queryClient: QueryClient,
): Promise<boolean> {
  if (target.id !== savedOrganizationId) {
    await persistOrganizationSelection(target.id);
    await queryClient.fetchQuery({ ...userProfileQueryOptions(), staleTime: 0 });
  }
  const shownOrganizationId = getCurrentOrganizationId();
  rememberTabOrganization(target.id);
  return shownOrganizationId !== undefined && shownOrganizationId !== target.id;
}
